import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
  Filler,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { colormaps, defaultColormap } from "./colormaps";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

const WORD_LANE = {
  heightDesktop: 32,
  heightMobile: 28,
  gapFromPlot: 3,
  fontDesktop: 14,
  fontMobile: 12,
  padXDesktop: 9,
  padXMobile: 7,
  radius: 7,
  hoverLift: 1.5,
  denseHitPaddingDesktop: 10,
  denseHitPaddingMobile: 8,
};

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function buildContinuousGuideCurve(xs, ys) {
  if (!xs?.length || !ys?.length) return [];

  const out = new Array(ys.length).fill(null);

  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    out[i] = y != null && Number.isFinite(Number(y)) ? Number(y) : null;
  }

  let firstValid = -1;
  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) {
      firstValid = i;
      break;
    }
  }
  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) out[i] = out[firstValid];

  let lastValid = -1;
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null) {
      lastValid = i;
      break;
    }
  }
  for (let i = lastValid + 1; i < out.length; i++) out[i] = out[lastValid];

  let i = firstValid;
  while (i < out.length) {
    if (out[i] != null) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;
    while (end < out.length && out[end] == null) end++;
    if (end >= out.length) break;

    const x1 = Number(xs[start]);
    const x2 = Number(xs[end]);
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = Number(xs[k]);
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  const smoothPass = (arr) => {
    const next = [...arr];
    for (let j = 1; j < arr.length - 1; j++) {
      next[j] = (arr[j - 1] + arr[j] + arr[j + 1]) / 3;
    }
    return next;
  };

  let smoothed = out.map((v) => Number(v));
  smoothed = smoothPass(smoothed);
  smoothed = smoothPass(smoothed);

  return xs.map((x, idx) => ({
    x: Number(x),
    y: smoothed[idx],
  }));
}

function interpolateOnCurve(points, xTarget) {
  if (!points?.length || !Number.isFinite(Number(xTarget))) return null;
  if (points.length === 1) return { x: xTarget, y: points[0].y };

  if (xTarget <= points[0].x) return { x: xTarget, y: points[0].y };
  if (xTarget >= points[points.length - 1].x) {
    return { x: xTarget, y: points[points.length - 1].y };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);

    if (xTarget >= minX && xTarget <= maxX) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;
      return {
        x: xTarget,
        y: p1.y + (p2.y - p1.y) * t,
      };
    }
  }

  return { x: xTarget, y: points[points.length - 1].y };
}

function toTransparentRgba(color, alpha = 0.4) {
  if (!color) return `rgba(100, 100, 100, ${alpha})`;

  if (color.startsWith("rgba(")) {
    const inside = color.slice(5, -1);
    const parts = inside.split(",").map((p) => p.trim());
    if (parts.length >= 3) {
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
    }
  }

  if (color.startsWith("rgb(")) {
    const inside = color.slice(4, -1);
    return `rgba(${inside}, ${alpha})`;
  }

  return color;
}

function interpolateRawVoicedAtTime(xs, ys, xTarget) {
  if (!xs?.length || !ys?.length || !Number.isFinite(Number(xTarget))) {
    return null;
  }

  const target = Number(xTarget);
  const count = Math.min(xs.length, ys.length);

  for (let i = 0; i < count - 1; i++) {
    const x1 = Number(xs[i]);
    const x2 = Number(xs[i + 1]);
    if (!Number.isFinite(x1) || !Number.isFinite(x2)) continue;
    if (target < x1 || target > x2) continue;

    const y1 = ys[i] == null ? null : Number(ys[i]);
    const y2 = ys[i + 1] == null ? null : Number(ys[i + 1]);

    // In raw diagnostic mode the ball disappears in unvoiced regions instead
    // of inventing a continuous guide curve through missing Praat frames.
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;

    const span = x2 - x1;
    const fraction = span === 0 ? 0 : (target - x1) / span;
    return {
      x: target,
      y: y1 + (y2 - y1) * fraction,
    };
  }

  return null;
}

function getRenderedWordChipBounds({
  word,
  xScale,
  chartArea,
  clipMin,
  clipMax,
}) {
  const startC = clipMin == null ? word.start : Math.max(word.start, clipMin);
  const endC = clipMax == null ? word.end : Math.min(word.end, clipMax);
  if (!(endC > startC)) return null;

  const x1 = xScale.getPixelForValue(startC);
  const x2 = xScale.getPixelForValue(endC);

  const left = clamp(
    Math.min(x1, x2),
    chartArea.left,
    chartArea.right
  );
  const right = clamp(
    Math.max(x1, x2),
    chartArea.left,
    chartArea.right
  );

  if (!(right > left)) return null;

  return {
    left,
    right,
    center: left + (right - left) / 2,
    width: right - left,
  };
}

function sameWord(a, b) {
  return Boolean(
    a &&
      b &&
      Math.abs(Number(a.start) - Number(b.start)) < 0.00001 &&
      Math.abs(Number(a.end) - Number(b.end)) < 0.00001
  );
}

function fitWordLabel(ctx, rawText, availableWidth) {
  const text = String(rawText ?? "").trim();
  if (!text || availableWidth < 9) return "";

  if (ctx.measureText(text).width <= availableWidth) {
    return text;
  }

  // Very dense words keep the duration-accurate button but reduce the label.
  if (availableWidth < 18) {
    return "";
  }

  if (availableWidth < 28) {
    const firstCharacter = Array.from(text)[0] || "";
    return ctx.measureText(firstCharacter).width <= availableWidth
      ? firstCharacter
      : "";
  }

  let truncated = text;

  while (
    truncated.length > 1 &&
    ctx.measureText(`${truncated}…`).width > availableWidth
  ) {
    truncated = truncated.slice(0, -1);
  }

  return truncated.length ? `${truncated}…` : "";
}

function buildCurveSlicePoints(xs, ys, start, end) {
  const sliceStart = Number(start);
  const sliceEnd = Number(end);

  if (
    !Number.isFinite(sliceStart) ||
    !Number.isFinite(sliceEnd) ||
    sliceEnd <= sliceStart ||
    !xs?.length ||
    !ys?.length
  ) {
    return [];
  }

  const points = [];
  const first = interpolateRawVoicedAtTime(xs, ys, sliceStart);

  points.push({
    x: sliceStart,
    y: first?.y ?? null,
  });

  const count = Math.min(xs.length, ys.length);

  for (let index = 0; index < count; index += 1) {
    const x = Number(xs[index]);

    if (!Number.isFinite(x) || x <= sliceStart || x >= sliceEnd) {
      continue;
    }

    const rawY = ys[index];
    const y = rawY == null ? null : Number(rawY);

    points.push({
      x,
      y: Number.isFinite(y) ? y : null,
    });
  }

  const last = interpolateRawVoicedAtTime(xs, ys, sliceEnd);

  points.push({
    x: sliceEnd,
    y: last?.y ?? null,
  });

  return points;
}

function makeLanePlugin(getLanePx) {
  return {
    id: "lanePlugin",

    beforeLayout(chart, _args, opts) {
      const showWordLane = opts?.showWordLane ?? true;
      const lanePx = showWordLane ? getLanePx(!!opts?.isMobile) : 0;
      const x = chart.scales?.x;
      if (!x) return;

      const prevAfterFit = x.options.afterFit;

      x.options.afterFit = (scale) => {
        if (typeof prevAfterFit === "function") prevAfterFit(scale);

        if (scale.$laneBaseHeight == null) {
          scale.$laneBaseHeight = scale.height || 0;
        }
        scale.height = scale.$laneBaseHeight + lanePx;
      };
    },

    beforeDatasetsDraw(chart, _args, pluginOptions) {
      const {
        hoveredWord = null,
        showWordLane = true,
        isMobile = false,
      } = pluginOptions || {};

      const { ctx, chartArea, scales } = chart;
      const xScale = scales?.x;

      if (!ctx || !chartArea || !xScale || !hoveredWord) return;

      const laneH = isMobile
        ? WORD_LANE.heightMobile
        : WORD_LANE.heightDesktop;

      const plotBottom = showWordLane
        ? chartArea.bottom - laneH - WORD_LANE.gapFromPlot
        : chartArea.bottom;

      const rawLeft = xScale.getPixelForValue(
        Number(hoveredWord.start)
      );
      const rawRight = xScale.getPixelForValue(
        Number(hoveredWord.end)
      );

      const left = clamp(
        Math.min(rawLeft, rawRight),
        chartArea.left,
        chartArea.right
      );
      const right = clamp(
        Math.max(rawLeft, rawRight),
        chartArea.left,
        chartArea.right
      );

      if (!(right > left)) return;

      ctx.save();
      ctx.beginPath();
      ctx.rect(
        chartArea.left,
        chartArea.top,
        chartArea.right - chartArea.left,
        Math.max(0, plotBottom - chartArea.top)
      );
      ctx.clip();

      const gradient = ctx.createLinearGradient(left, 0, right, 0);
      gradient.addColorStop(0, "rgba(37,99,235,0.010)");
      gradient.addColorStop(0.16, "rgba(37,99,235,0.046)");
      gradient.addColorStop(0.84, "rgba(37,99,235,0.046)");
      gradient.addColorStop(1, "rgba(37,99,235,0.010)");

      ctx.fillStyle = gradient;
      ctx.fillRect(
        left,
        chartArea.top,
        Math.max(1, right - left),
        Math.max(0, plotBottom - chartArea.top)
      );

      ctx.restore();
    },

    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        wordIntervals = [],
        activeWord = null,
        hoveredWord = null,
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = null,
        showBall = false,
        showWordLabels = true,
        showWordLane = true,
        isMobile = false,
        xWindowMin = null,
        xWindowMax = null,
      } = pluginOptions || {};

      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { top, bottom, left, right } = chartArea;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const gap = WORD_LANE.gapFromPlot;
      const laneTop = bottom - laneH;
      const laneBottom = bottom;

      const fontSize = isMobile ? WORD_LANE.fontMobile : WORD_LANE.fontDesktop;
      const clipMin =
        Number.isFinite(Number(xWindowMin)) ? Number(xWindowMin) : null;
      const clipMax =
        Number.isFinite(Number(xWindowMax)) ? Number(xWindowMax) : null;

      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, right - left, bottom - top);
      ctx.clip();

      if (showWordLane) {
        ctx.fillStyle = "rgba(15,23,42,0.035)";
        ctx.strokeStyle = "rgba(148,163,184,0.30)";
        ctx.lineWidth = 1;
        roundRect(ctx, left, laneTop, right - left, laneH, 12);
        ctx.fill();
        ctx.stroke();

        ctx.strokeStyle = "rgba(148,163,184,0.20)";
        ctx.beginPath();
        ctx.moveTo(left, laneTop - gap + 0.5);
        ctx.lineTo(right, laneTop - gap + 0.5);
        ctx.stroke();

        const chipY = laneTop + 3;
        const chipH = laneH - 6;

        ctx.font = `600 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";

        let hoverLabel = null;

        for (const w of wordIntervals) {
          const bounds = getRenderedWordChipBounds({
            word: w,
            xScale,
            chartArea,
            clipMin,
            clipMax,
          });

          if (!bounds) continue;

          // A one-pixel inset separates adjacent words without changing the
          // timeline meaning. The outer bounds still represent exact duration.
          const buttonX = bounds.left + 0.5;
          const buttonW = Math.max(1, bounds.width - 1);
          const buttonRadius = Math.min(
            WORD_LANE.radius,
            buttonW / 2,
            chipH / 2
          );

          const isActive = sameWord(activeWord, w);
          const isHovered = sameWord(hoveredWord, w);
          const lift = isHovered ? WORD_LANE.hoverLift : 0;

          ctx.save();

          if (isActive && isHovered) {
            // Playback stays understated; hover remains the stronger,
            // intentional interaction signal.
            ctx.fillStyle = "rgba(236,253,245,0.96)";
            ctx.strokeStyle = "rgba(37,99,235,0.78)";
            ctx.lineWidth = 1.3;
            ctx.shadowColor = "rgba(37,99,235,0.13)";
            ctx.shadowBlur = 7;
            ctx.shadowOffsetY = 2;
          } else if (isHovered) {
            ctx.fillStyle = "rgba(239,246,255,0.96)";
            ctx.strokeStyle = "rgba(37,99,235,0.82)";
            ctx.lineWidth = 1.25;
            ctx.shadowColor = "rgba(37,99,235,0.14)";
            ctx.shadowBlur = 7;
            ctx.shadowOffsetY = 2;
          } else if (isActive) {
            ctx.fillStyle = "rgba(209,250,229,0.50)";
            ctx.strokeStyle = "rgba(5,150,105,0.62)";
            ctx.lineWidth = 1.05;
          } else {
            ctx.fillStyle = "rgba(248,250,252,0.86)";
            ctx.strokeStyle = "rgba(148,163,184,0.58)";
            ctx.lineWidth = 1;
          }

          roundRect(
            ctx,
            buttonX,
            chipY - lift,
            buttonW,
            chipH,
            buttonRadius
          );
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          roundRect(
            ctx,
            buttonX,
            chipY - lift,
            buttonW,
            chipH,
            buttonRadius
          );
          ctx.stroke();

          ctx.restore();

          if (showWordLabels) {
            const available = Math.max(
              0,
              buttonW -
                (isMobile
                  ? WORD_LANE.padXMobile
                  : WORD_LANE.padXDesktop) *
                  2
            );

            const label = fitWordLabel(ctx, w.text, available);

            if (label) {
              ctx.fillStyle = isHovered
                ? "rgba(29,78,216,1)"
                : isActive
                ? "rgba(4,120,87,0.96)"
                : "rgba(51,65,85,0.92)";

              ctx.fillText(
                label,
                bounds.center,
                chipY + chipH / 2 - lift
              );
            }
          }

          if (isHovered) {
            const fullText = String(w.text ?? "").trim();
            const fullTextWidth = ctx.measureText(fullText).width;
            const needsFloatingLabel =
              fullText &&
              (buttonW < fullTextWidth + 18 || buttonW < 34);

            if (needsFloatingLabel) {
              hoverLabel = {
                text: fullText,
                center: bounds.center,
                laneTop,
              };
            }
          }
        }

        if (hoverLabel) {
          const tooltipFontSize = isMobile ? 12 : 13;
          ctx.save();
          ctx.font = `650 ${tooltipFontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;

          const textWidth = ctx.measureText(hoverLabel.text).width;
          const tooltipW = Math.min(
            right - left - 12,
            Math.max(48, textWidth + 22)
          );
          const tooltipH = isMobile ? 27 : 29;
          const tooltipX = clamp(
            hoverLabel.center - tooltipW / 2,
            left + 6,
            right - 6 - tooltipW
          );
          const tooltipY = Math.max(
            top + 6,
            hoverLabel.laneTop - tooltipH - 8
          );

          ctx.shadowColor = "rgba(15,23,42,0.15)";
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 3;
          ctx.fillStyle = "rgba(255,255,255,0.98)";
          roundRect(ctx, tooltipX, tooltipY, tooltipW, tooltipH, 8);
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;
          ctx.strokeStyle = "rgba(59,130,246,0.46)";
          ctx.lineWidth = 1;
          roundRect(ctx, tooltipX, tooltipY, tooltipW, tooltipH, 8);
          ctx.stroke();

          const pointerX = clamp(
            hoverLabel.center,
            tooltipX + 10,
            tooltipX + tooltipW - 10
          );

          ctx.beginPath();
          ctx.moveTo(pointerX - 5, tooltipY + tooltipH);
          ctx.lineTo(pointerX + 5, tooltipY + tooltipH);
          ctx.lineTo(pointerX, tooltipY + tooltipH + 5);
          ctx.closePath();
          ctx.fillStyle = "rgba(255,255,255,0.98)";
          ctx.fill();

          ctx.beginPath();
          ctx.moveTo(pointerX - 5, tooltipY + tooltipH);
          ctx.lineTo(pointerX, tooltipY + tooltipH + 5);
          ctx.lineTo(pointerX + 5, tooltipY + tooltipH);
          ctx.strokeStyle = "rgba(59,130,246,0.38)";
          ctx.stroke();

          ctx.fillStyle = "rgba(30,64,175,1)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(
            hoverLabel.text,
            tooltipX + tooltipW / 2,
            tooltipY + tooltipH / 2
          );
          ctx.restore();
        }
      }

      if (cursorX != null) {
        const xC = xScale.getPixelForValue(cursorX);
        if (xC >= left && xC <= right) {
          ctx.strokeStyle = "rgba(255,255,255,0.90)";
          ctx.lineWidth = isMobile ? 3 : 3.5;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();

          ctx.strokeStyle = "rgba(16,185,129,1)";
          ctx.lineWidth = isMobile ? 1.6 : 1.8;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();
        }
      }

      if (
        showBall &&
        cursorEnvelopePoint &&
        Number.isFinite(Number(cursorEnvelopePoint.x)) &&
        Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        const r = ballRadius ?? (isMobile ? 8.5 : 12);
        const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
        const py = clamp(
          yScale.getPixelForValue(cursorEnvelopePoint.y),
          top + r + 2,
          bottom - r - 2
        );

        if (px >= left && px <= right && py >= top - 6) {
          ctx.save();
          ctx.shadowColor = "rgba(16,185,129,0.48)";
          ctx.shadowBlur = isMobile ? 14 : 18;
          ctx.beginPath();
          ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(16,185,129,0.95)";
          ctx.fill();
          ctx.restore();

          const bodyGrad = ctx.createRadialGradient(
            px - r * 0.32,
            py - r * 0.34,
            r * 0.18,
            px,
            py,
            r
          );
          bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
          bodyGrad.addColorStop(0.18, "rgba(134,239,172,1)");
          bodyGrad.addColorStop(0.48, "rgba(34,197,94,1)");
          bodyGrad.addColorStop(0.78, "rgba(16,185,129,1)");
          bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

          ctx.beginPath();
          ctx.arc(px, py, r, 0, Math.PI * 2);
          ctx.fillStyle = bodyGrad;
          ctx.fill();

          ctx.lineWidth = 2.4;
          ctx.strokeStyle = "rgba(255,255,255,0.92)";
          ctx.stroke();

          const rimGrad = ctx.createRadialGradient(
            px - r * 0.2,
            py - r * 0.2,
            r * 0.2,
            px,
            py,
            r * 1.15
          );
          rimGrad.addColorStop(0, "rgba(255,255,255,0)");
          rimGrad.addColorStop(0.75, "rgba(255,255,255,0)");
          rimGrad.addColorStop(1, "rgba(255,255,255,0.22)");

          ctx.beginPath();
          ctx.arc(px, py, r, 0, Math.PI * 2);
          ctx.fillStyle = rimGrad;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(
            px - r * 0.34,
            py - r * 0.38,
            Math.max(1.8, r * 0.26),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(255,255,255,0.88)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(
            px - r * 0.08,
            py - r * 0.14,
            Math.max(1.2, r * 0.11),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(255,255,255,0.32)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(
            px + r * 0.28,
            py + r * 0.34,
            Math.max(1.4, r * 0.18),
            0,
            Math.PI * 2
          );
          ctx.fillStyle = "rgba(0,0,0,0.10)";
          ctx.fill();
        }
      }

      ctx.restore();
    },
  };
}

const ColorBar = ({ colormap, min = 41, max = 86 }) => {
  const steps = 100;
  const gradientStops = [];

  for (let index = 0; index <= steps; index += 1) {
    const normalized = index / steps;
    const mappedIntensity = normalized * 90;
    const color =
      colormap?.getColor?.(mappedIntensity) ||
      "rgb(100, 100, 100)";

    gradientStops.push(
      `${color} ${normalized * 100}%`
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <span>{min}dB</span>

        <div
          className="flex-1 h-4 rounded"
          style={{
            background: `linear-gradient(to right, ${gradientStops.join(
              ", "
            )})`,
          }}
          aria-hidden="true"
        />

        <span>{max}dB</span>
      </div>

      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>50dB</span>
        <span>60dB</span>
        <span>70dB</span>
        <span>80dB</span>
      </div>
    </div>
  );
};

export default function PitchIntensityAnimation({
  intensity,
  pitch,
  segment,
  audioRef,
  broadcastTime = null,
  speed = 0.25,
  showPitch = true,
  selectedColormap = defaultColormap || "speechStable",
  onColormapChange,
  onWordClick,
}) {
  const chartRef = useRef(null);
  const [internalColormap, setInternalColormap] = useState(selectedColormap);
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);
  const [hoveredWord, setHoveredWord] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);
  const broadcastTimeRef = useRef(broadcastTime);
  const lastPlayingRef = useRef(null);

  const wordPlayRafRef = useRef(null);
  const wordPlayEndRef = useRef(null);

  const currentColormap = onColormapChange ? selectedColormap : internalColormap;

  const pitchXs = useMemo(() => pitch?.xs || [], [pitch]);
  const pitchYsRaw = useMemo(() => pitch?.ys || [], [pitch]);
  const intensityXs = useMemo(() => intensity?.xs || [], [intensity]);
  const intensityYs = useMemo(() => intensity?.ys || [], [intensity]);

  const segmentDuration = useMemo(() => {
    const start = Number(segment?.start ?? 0);
    const end = Number(segment?.end ?? start);
    return Math.max(0, end - start);
  }, [segment]);

  const wordIntervals = useMemo(() => {
    const segStart = Number(segment?.start ?? 0);
    const segEnd = Number(segment?.end ?? segStart);
    const segDuration = Math.max(0, segEnd - segStart);

    return (
      segment?.words?.map((w) => {
        const start = clamp(Number(w.start) - segStart, 0, segDuration);
        const end = clamp(Number(w.end) - segStart, start, segDuration);

        return {
          start,
          end,
          text: String(w.text ?? ""),
        };
      }) || []
    );
  }, [segment]);

  const shouldShowWordLane = wordIntervals.length > 0;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const stopWordPlayback = useCallback(() => {
    if (wordPlayRafRef.current) {
      cancelAnimationFrame(wordPlayRafRef.current);
      wordPlayRafRef.current = null;
    }
    wordPlayEndRef.current = null;
  }, []);

  const playWordRangePrecise = useCallback(
    (relStart, relEnd) => {
      const a = audioRef?.current;
      if (!a || !segment) return;

      const segStart = Number(segment.start ?? 0);
      const segEnd = Number(segment.end ?? segStart);
      const segDuration = Math.max(0, segEnd - segStart);

      const END_GUARD = 0.05;
      const STOP_EARLY = 0.012;

      const safeRelStart = clamp(Number(relStart), 0, segDuration);
      const maxRelEnd = Math.max(
        safeRelStart,
        Math.max(0, segDuration - END_GUARD)
      );
      const safeRelEnd = clamp(Number(relEnd), safeRelStart, maxRelEnd);

      const absStart = segStart + safeRelStart;
      const absEnd = segStart + safeRelEnd;

      stopWordPlayback();

      a.pause();
      a.currentTime = absStart;
      wordPlayEndRef.current = absEnd;

      const playPromise = a.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }

      const stopNow = () => {
        const aa = audioRef?.current;
        if (!aa) return;
        aa.pause();
        aa.currentTime = Math.min(absEnd - 0.001, segEnd - END_GUARD);
        stopWordPlayback();
      };

      const tick = () => {
        const aa = audioRef?.current;
        const targetEnd = wordPlayEndRef.current;

        if (!aa || targetEnd == null) return;

        if (aa.currentTime >= targetEnd - STOP_EARLY) {
          stopNow();
          return;
        }

        wordPlayRafRef.current = requestAnimationFrame(tick);
      };

      wordPlayRafRef.current = requestAnimationFrame(tick);
    },
    [audioRef, segment, stopWordPlayback]
  );

  useEffect(() => {
    broadcastTimeRef.current = broadcastTime;
  }, [broadcastTime]);

  useEffect(() => {
    const segmentStart = Number(segment?.start ?? 0);
    let cancelled = false;

    const readRelativeTime = () => {
      // NarratorIntonation already computes a live segment-relative clock and
      // passes it as broadcastTime. Prefer it because it remains valid when the
      // underlying audio element or source is replaced.
      const externalTime = Number(broadcastTimeRef.current);

      if (Number.isFinite(externalTime)) {
        return clamp(externalTime, 0, segmentDuration);
      }

      // Fallback for direct component usage without broadcastTime.
      const audio = audioRef?.current;

      if (!audio || !Number.isFinite(Number(audio.currentTime))) {
        return clamp(
          Number(displayTimeRef.current) || 0,
          0,
          segmentDuration
        );
      }

      return clamp(
        Number(audio.currentTime) - segmentStart,
        0,
        segmentDuration
      );
    };

    const update = () => {
      if (cancelled) return;

      const nextTime = readRelativeTime();
      const previousTime = displayTimeRef.current;

      displayTimeRef.current = nextTime;

      if (
        previousTime == null ||
        Math.abs(nextTime - previousTime) > 0.0001
      ) {
        setAnimatedTime(nextTime);
      }

      // Read audioRef.current on every frame rather than binding playback state
      // to whichever element happened to exist when the effect mounted.
      const audio = audioRef?.current;
      const nextPlaying = Boolean(
        audio &&
        !audio.paused &&
        !audio.ended &&
        Number.isFinite(Number(audio.currentTime))
      );

      if (lastPlayingRef.current !== nextPlaying) {
        lastPlayingRef.current = nextPlaying;
        setIsAudioPlaying(nextPlaying);
      }

      rafRef.current = requestAnimationFrame(update);
    };

    const initialTime = readRelativeTime();
    displayTimeRef.current = initialTime;
    setAnimatedTime(initialTime);

    const initialAudio = audioRef?.current;
    const initialPlaying = Boolean(
      initialAudio &&
      !initialAudio.paused &&
      !initialAudio.ended &&
      Number.isFinite(Number(initialAudio.currentTime))
    );

    lastPlayingRef.current = initialPlaying;
    setIsAudioPlaying(initialPlaying);

    rafRef.current = requestAnimationFrame(update);

    return () => {
      cancelled = true;

      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      lastPlayingRef.current = null;
    };
  }, [
    audioRef,
    segment?.start,
    segmentDuration,
  ]);

  useEffect(() => {
    return () => stopWordPlayback();
  }, [stopWordPlayback]);

  useEffect(() => {
    stopWordPlayback();
  }, [segment, stopWordPlayback]);

  const handleColormapChange = useCallback(
    (newColormap) => {
      if (onColormapChange) {
        onColormapChange(newColormap);
      } else {
        setInternalColormap(newColormap);
      }
    },
    [onColormapChange]
  );

  const pitchYs = useMemo(() => {
    const out = new Array(pitchYsRaw.length);
    for (let i = 0; i < pitchYsRaw.length; i++) {
      const v = Number(pitchYsRaw[i]);
      out[i] = isFiniteNum(v) && v > 0 ? v : null;
    }
    return out;
  }, [pitchYsRaw]);

  const smoothedPitchYs = useMemo(() => {
    const data = pitchYs;
    const windowSize = isMobile ? 2 : 3;
    const smoothed = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i] == null) {
        smoothed.push(null);
        continue;
      }

      let sum = 0;
      let count = 0;

      for (
        let j = Math.max(0, i - windowSize);
        j <= Math.min(data.length - 1, i + windowSize);
        j++
      ) {
        if (data[j] != null) {
          sum += data[j];
          count++;
        }
      }

      smoothed.push(count ? sum / count : null);
    }

    return smoothed;
  }, [pitchYs, isMobile]);

  const voicedValues = useMemo(() => {
    return smoothedPitchYs.filter((v) => v != null && isFiniteNum(v));
  }, [smoothedPitchYs]);

  const yMin = useMemo(() => {
    let m = Infinity;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.min(m, voicedValues[i]);
    }
    return Number.isFinite(m) ? m : 0;
  }, [voicedValues]);

  const yMax = useMemo(() => {
    let m = 1;
    for (let i = 0; i < voicedValues.length; i++) {
      m = Math.max(m, voicedValues[i]);
    }
    return m;
  }, [voicedValues]);

  const yRange = useMemo(() => Math.max(1, yMax - yMin), [yMax, yMin]);

  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!pitchXs.length || !smoothedPitchYs.length) {
      return { voicedXMin: 0, voicedXMax: 1 };
    }

    let first = -1;
    let last = -1;

    for (let i = 0; i < smoothedPitchYs.length; i++) {
      if (smoothedPitchYs[i] != null) {
        first = i;
        break;
      }
    }

    for (let i = smoothedPitchYs.length - 1; i >= 0; i--) {
      if (smoothedPitchYs[i] != null) {
        last = i;
        break;
      }
    }

    if (first === -1 || last === -1) {
      return {
        voicedXMin: pitchXs[0] ?? 0,
        voicedXMax: pitchXs[pitchXs.length - 1] ?? 1,
      };
    }

    return {
      voicedXMin: Number(pitchXs[first]),
      voicedXMax: Number(pitchXs[last]),
    };
  }, [pitchXs, smoothedPitchYs]);

  const cursorX = useMemo(() => {
    if (!pitchXs.length) return null;
    const t = Number(animatedTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [animatedTime, pitchXs.length, voicedXMin, voicedXMax]);

  const showBall = true;

  const predictedCursorX = useMemo(() => {
    if (!pitchXs.length) return null;

    const leadSeconds =
      speed <= 0.25 ? 0.01 : speed <= 0.5 ? 0.008 : 0.005;

    return clamp(
      (cursorX ?? voicedXMin) + leadSeconds,
      voicedXMin,
      voicedXMax
    );
  }, [cursorX, pitchXs.length, voicedXMin, voicedXMax, speed]);

  const guideCurve = useMemo(() => {
    return buildContinuousGuideCurve(pitchXs, smoothedPitchYs);
  }, [pitchXs, smoothedPitchYs]);

  const cursorEnvelopePoint = useMemo(() => {
    if (!guideCurve.length || predictedCursorX == null) return null;

    const p = interpolateOnCurve(guideCurve, predictedCursorX);
    if (!p) return null;

    const normalizedHeight = yRange > 0 ? (p.y - yMin) / yRange : 0;
    const baseLift = Math.max(8, yRange * 0.065);
    const peakLift =
      Math.max(0, normalizedHeight) * Math.max(2.2, yRange * 0.03);

    return {
      x: p.x,
      y: Math.max(0, p.y + baseLift + peakLift),
    };
  }, [guideCurve, predictedCursorX, yMin, yRange]);

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const activeWord = useMemo(() => {
    if (!isAudioPlaying || !shouldShowWordLane || cursorX == null) {
      return null;
    }
    const EPS = 0.0005;

    for (let i = 0; i < wordIntervals.length; i++) {
      const w = wordIntervals[i];
      const isLast = i === wordIntervals.length - 1;

      if (
        (isLast && cursorX >= w.start - EPS && cursorX <= w.end + EPS) ||
        (!isLast && cursorX >= w.start - EPS && cursorX < w.end - EPS)
      ) {
        return w;
      }
    }

    return null;
  }, [
    isAudioPlaying,
    cursorX,
    wordIntervals,
    shouldShowWordLane,
  ]);

  const shouldShowWordLabels = useMemo(() => {
    return showWordLabels && shouldShowWordLane;
  }, [showWordLabels, shouldShowWordLane]);

  const lanePxGetter = useMemo(() => {
    return (mobile) =>
      (mobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop) +
      WORD_LANE.gapFromPlot;
  }, []);

  const lanePlugin = useMemo(() => makeLanePlugin(lanePxGetter), [lanePxGetter]);

  const intensityLookup = useMemo(() => {
    const out = new Array(intensityXs.length);
    for (let i = 0; i < intensityXs.length; i++) {
      out[i] = {
        x: Number(intensityXs[i]),
        y: Number(intensityYs[i] ?? 41),
      };
    }
    return out;
  }, [intensityXs, intensityYs]);

  const fillGradientStops = useMemo(() => {
    const cmap =
      colormaps[currentColormap] ||
      colormaps[defaultColormap] ||
      colormaps.speechStable;

    if (!cmap?.getColor || !intensityLookup.length) {
      return [{ offset: 0, color: "rgba(100, 100, 100, 0.4)" }];
    }

    const fixedMin = 41;
    const fixedMax = 86;
    const fixedRange = fixedMax - fixedMin || 1;
    const steps = 48;
    const out = [];

    let j = 0;

    for (let i = 0; i <= steps; i++) {
      const xPercent = i / steps;
      const xValue = voicedXMin + xPercent * (voicedXMax - voicedXMin);

      while (
        j < intensityLookup.length - 2 &&
        intensityLookup[j + 1].x < xValue
      ) {
        j++;
      }

      let closestIntensity = intensityLookup[j]?.y ?? fixedMin;

      const a = intensityLookup[j];
      const b = intensityLookup[j + 1];

      if (a && b) {
        const da = Math.abs(a.x - xValue);
        const db = Math.abs(b.x - xValue);
        closestIntensity = da <= db ? a.y : b.y;
      }

      const clampedIntensity = clamp(closestIntensity, fixedMin, fixedMax);
      const normalizedIntensity =
        (clampedIntensity - fixedMin) / fixedRange;
      const mappedIntensity = normalizedIntensity * 90;

      let color = "rgba(100, 100, 100, 0.4)";
      try {
        color = toTransparentRgba(cmap.getColor(mappedIntensity), 0.4);
      } catch {
        color = "rgba(100, 100, 100, 0.4)";
      }

      out.push({ offset: xPercent, color });
    }

    return out;
  }, [currentColormap, intensityLookup, voicedXMin, voicedXMax]);

  const data = useMemo(() => {
    const datasets = [
      {
        label: "Pitch (Hz)",
        data: pitchXs.map((x, index) => ({
          x,
          y: smoothedPitchYs[index],
        })),
        borderColor: hoveredWord
          ? "rgba(37,99,235,0.50)"
          : "rgba(37,99,235,1)",
        borderWidth: isMobile ? 1.75 : 2.25,
        tension: isMobile ? 0.25 : 0.42,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        showLine: true,
        spanGaps: false,
        borderCapStyle: "round",
        borderJoinStyle: "round",
        fill: !!showPitch,
        backgroundColor: (context) => {
          if (!showPitch) return "rgba(0,0,0,0)";

          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return "rgba(100,100,100,0.4)";

          const gradient = ctx.createLinearGradient(
            chartArea.left,
            0,
            chartArea.right,
            0
          );

          for (const stop of fillGradientStops) {
            gradient.addColorStop(stop.offset, stop.color);
          }

          return gradient;
        },
        datalabels: { display: false },
      },
    ];

    if (hoveredWord) {
      datasets.push({
        label: "Hovered word pitch",
        data: buildCurveSlicePoints(
          pitchXs,
          smoothedPitchYs,
          hoveredWord.start,
          hoveredWord.end
        ),
        borderColor: "rgba(37,99,235,1)",
        borderWidth: isMobile ? 2.8 : 3.35,
        tension: isMobile ? 0.25 : 0.42,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        showLine: true,
        spanGaps: false,
        borderCapStyle: "round",
        borderJoinStyle: "round",
        fill: false,
        datalabels: { display: false },
      });
    }

    return { datasets };
  }, [
    pitchXs,
    smoothedPitchYs,
    isMobile,
    showPitch,
    fillGradientStops,
    hoveredWord,
  ]);

  const pickWordAtEvent = useCallback(
    (event, chart) => {
      if (!shouldShowWordLane || !chart) return null;

      const { chartArea, scales } = chart;
      const xScale = scales?.x;
      if (!xScale || !chartArea) return null;

      const position = getRelativePosition(event, chart);
      const xPixel = position.x;
      const yPixel = position.y;

      const laneHeight = isMobile
        ? WORD_LANE.heightMobile
        : WORD_LANE.heightDesktop;
      const laneTop = chartArea.bottom - laneHeight;
      const laneBottom = chartArea.bottom;

      if (
        xPixel < chartArea.left ||
        xPixel > chartArea.right ||
        yPixel < laneTop ||
        yPixel > laneBottom
      ) {
        return null;
      }

      const time = xScale.getValueForPixel(xPixel);

      if (Number.isFinite(time)) {
        const EPS = 0.0005;

        for (let index = 0; index < wordIntervals.length; index += 1) {
          const word = wordIntervals[index];
          const isLast = index === wordIntervals.length - 1;

          if (
            time >= word.start - EPS &&
            (isLast
              ? time <= word.end + EPS
              : time < word.end - EPS)
          ) {
            return word;
          }
        }
      }

      const hitPadding = isMobile
        ? WORD_LANE.denseHitPaddingMobile
        : WORD_LANE.denseHitPaddingDesktop;

      let best = null;

      for (const word of wordIntervals) {
        const bounds = getRenderedWordChipBounds({
          word,
          xScale,
          chartArea,
          clipMin: voicedXMin,
          clipMax: voicedXMax,
        });

        if (!bounds) continue;

        const distance =
          xPixel < bounds.left
            ? bounds.left - xPixel
            : xPixel > bounds.right
            ? xPixel - bounds.right
            : 0;

        if (
          distance <= hitPadding &&
          (!best || distance < best.distance)
        ) {
          best = { word, distance };
        }
      }

      return best?.word ?? null;
    },
    [
      isMobile,
      wordIntervals,
      shouldShowWordLane,
      voicedXMin,
      voicedXMax,
    ]
  );

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      normalized: true,
      parsing: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 0   } },

      onHover: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        setHoveredWord(w);
        if (chart?.canvas) {
          chart.canvas.style.cursor = w ? "pointer" : "default";
        }
      },

      onClick: (event, _elements, chart) => {
        const w = pickWordAtEvent(event, chart);
        if (!w) return;

        playWordRangePrecise(w.start, w.end);

        if (typeof onWordClick === "function") {
          onWordClick(w);
        }
      },

      scales: {
        x: {
          type: "linear",
          min: voicedXMin,
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          ticks: { display: false },
          title: { display: false },
        },
        y: {
          min: 0,
          max: yMax + Math.max(yRange * 0.34, 28),
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          title: {
            display: true,
            text: "Pitch (Hz)",
            color: "rgba(51,65,85,0.80)",
            font: { size: isMobile ? 12 : 13, weight: "600" },
          },
          ticks: {
            color: "rgba(51,65,85,0.70)",
            font: { size: isMobile ? 10 : 11 },
            maxTicksLimit: 5,
          },
        },
      },

      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
          filter: (context) =>
            context.dataset?.label !== "Hovered word pitch",
          callbacks: {
            afterBody: (context) => {
              const xValue = Number(context?.[0]?.parsed?.x);

              let closestIntensity = 0;
              let minDistance = Infinity;

              for (let i = 0; i < intensityLookup.length; i++) {
                const xi = intensityLookup[i].x;
                const yi = intensityLookup[i].y;
                const distance = Math.abs(xi - xValue);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestIntensity = yi;
                }
              }

              return [`Intensity: ${closestIntensity.toFixed(1)} dB`];
            },
          },
        },
        datalabels: { display: false },
        lanePlugin: {
          wordIntervals,
          activeWord,
          hoveredWord,
          cursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall,
          showWordLabels: shouldShowWordLabels,
          showWordLane: shouldShowWordLane,
          isMobile,
          xWindowMin: voicedXMin,
          xWindowMax: voicedXMax,
        },
      },

      interaction: { mode: undefined, axis: "x", intersect: false },
      elements: {
        point: { radius: 0, hoverRadius: 0, hitRadius: 0 },
      },
    };
  }, [
    isMobile,
    yMax,
    yRange,
    voicedXMin,
    voicedXMax,
    intensityLookup,
    wordIntervals,
    activeWord,
    hoveredWord,
    cursorX,
    cursorEnvelopePoint,
    ballRadius,
    showBall,
    shouldShowWordLabels,
    shouldShowWordLane,
    onWordClick,
    pickWordAtEvent,
    playWordRangePrecise,
  ]);

  if (!pitchXs.length || !intensityXs.length) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <div className="flex flex-col gap-2">
        <label
          className={`flex items-center gap-2 select-none ${
            isMobile ? "text-xs" : "text-sm"
          }`}
        >
          Color Map:
          <select
            value={currentColormap}
            onChange={(e) => handleColormapChange(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {Object.entries(colormaps).map(([key, cm]) => (
              <option key={key} value={key}>
                {cm.name} - {cm.description}
              </option>
            ))}
          </select>
        </label>

        <ColorBar
          colormap={
            colormaps[currentColormap] ||
            colormaps[defaultColormap] ||
            colormaps.speechStable
          }
          min={41}
          max={86}
        />
      </div>

      {isMobile && shouldShowWordLane && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels((v) => !v)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
        onMouseLeave={() => setHoveredWord(null)}
      >
        <Line
          ref={chartRef}
          data={data}
          options={options}
          plugins={[lanePlugin]}
        />
      </div>

      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Tap words in the lane to replay • Word labels:{" "}
          {shouldShowWordLane ? (showWordLabels ? "ON" : "OFF") : "HIDDEN"}
        </p>
      )}
    </div>
  );
}